/**
 * DeactivateUserModal — Leadership-only confirmation dialog for removing (deactivating) a user.
 *
 * Disables the Cognito account + global sign-out via useDeactivateUser. Not shown for the caller's
 * own account (the backend also blocks self-deactivation with a 403).
 */
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { useDeactivateUser } from "@/hooks/useUsers";
import { apiErrorMessage } from "@/lib/api";
import type { DeliverProUser } from "@/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function DeactivateUserModal({
  user,
  open,
  onOpenChange,
}: {
  user: DeliverProUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const deactivate = useDeactivateUser();

  const handleConfirm = (): void => {
    deactivate.mutate(
      { userId: user.id },
      {
        onSuccess: () => {
          toast.success(`${user.full_name} has been removed`);
          onOpenChange(false);
        },
        onError: (err) => {
          // Server explanation (LAST_LEADERSHIP_USER / SELF_DEACTIVATION_FORBIDDEN / …), not
          // axios' generic "Request failed with status code 409".
          toast.error(apiErrorMessage(err, "Failed to remove user"));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove {user.full_name}?</DialogTitle>
          <DialogDescription>
            This deactivates the account and signs the user out everywhere. They
            will no longer be able to sign in. You can reactivate them later.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deactivate.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleConfirm}
            disabled={deactivate.isPending}
          >
            {deactivate.isPending && (
              <Loader2 className="animate-spin" aria-hidden="true" />
            )}
            Remove user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeactivateUserModal;
